var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useRef, useEffect } from 'react';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import { useFetch, usePromise } from '@rad/hooks';
import { expand } from '@rad/utility/validation';
import { konstPromise } from '@rad/utility/promise';
import Card from '@rad/components/ui/card';
import Modal from '@rad/components/ui/modal';
import Input from '@rad/components/inputs/input';
import Dropdown from '@rad/components/inputs/dropdown';
import MultiSelect from '@rad/components/inputs/multi-select';
import { toPropertyForm, propertyFromCreateResult, mapPropertyToMapAttributes, } from '../../../../../types';
import { createDisplayConfig, assertGeographic, geographicPoint, isPoint, regridParcelLayerURL } from '../constants';
import { useMapCore } from '../hooks/useMapCore';
import { useMapLayers } from '../hooks/useMapLayers';
import { useMapToolbar } from '../hooks/useMapToolbar';
import { useMapEditing } from '../hooks/useMapEditing';
import { usePointPlacement } from '../hooks/usePointPlacement';
import { useViewportLoad, filtersGeoNone } from '../hooks/useViewportLoad';
import { useLegend } from '../hooks/useLegend';
import { MapToolbar } from './map-toolbar';
import { PropertyPopup } from './parcel-popup';
import { EditingToolbar } from './editing-toolbar';
import { SaveToolbar } from './save-toolbar';
import { PlacementBanner } from './placement-banner';
import { MapAttribution } from './map-attribution';
import { LegendPanel } from './legend-panel';
import { updateAreaGeometry } from '../../../../../common/network/district';
import { filtersDefault, update as updateProperty, getPropertyTags, setPropertyTags, getProperties } from '../../../../../common/network/property';
import { createParcel, findNearbyParcels, updateParcel } from '../../../../../common/network/parcels';
import { getParcelKinds } from '../../../../../common/network/parcel-kind';
import useDistrict from '../../../../../hooks/useDistrict';
import { AddAddressModal } from '../../add-address-modal';
import PropertyAddEdit from '../../../../admin/properties/forms/add-edit';
import { usStates } from '../../../../../common/constants';
const emptyParcelForm = {
    street: '',
    city: '',
    state: '',
    zip: '',
};
export const EditableArcGISMap = (props) => {
    var _a, _b, _c, _d;
    const { config: configOverride, className } = props;
    const config = useMemo(() => configOverride !== null && configOverride !== void 0 ? configOverride : createDisplayConfig({
        height: '100%',
        showBasemapSelector: true,
        zoom: undefined,
    }), [configOverride]);
    const tileLayers = useMemo(() => [
        {
            url: regridParcelLayerURL,
            displayName: "Parcel Borders",
            showToggle: true,
            opacity: 0.6,
            minScale: 25000,
            maxScale: 0,
            visible: true,
        }
    ], []);
    // --- Data fetching ---
    const { district } = useDistrict();
    const [parcelKindsPromise] = useFetch(getParcelKinds());
    const [propertyTagsPromise] = useFetch(getPropertyTags());
    // --- Editing state ---
    const [editMode, setEditMode] = useState('areas');
    const [selectedId, setSelectedId] = useState(null);
    const [editedGeometries, setEditedGeometries] = useState(new Map());
    // --- Parcel creation state ---
    const [isAddingParcel, setIsAddingParcel] = useState(false);
    const [parcelForm, setParcelForm] = useState(emptyParcelForm);
    const [selectedParcelKind, setSelectedParcelKind] = useState('');
    const parcelKinds = usePromise(parcelKindsPromise);
    useEffect(() => {
        if ((parcelKinds === null || parcelKinds === void 0 ? void 0 : parcelKinds.length) && !selectedParcelKind) {
            setSelectedParcelKind(parcelKinds[0].name);
        }
    }, [parcelKinds]);
    const [parcelPoint, setParcelPoint] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [editedParcelPoints, setEditedParcelPoints] = useState(new Map());
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [isPropertiesPanelOpen, setIsPropertiesPanelOpen] = useState(true);
    const [propertySearch, setPropertySearch] = useState('');
    const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
    const [selectedParcel, setSelectedParcel] = useState(null);
    const [propertyForm, setPropertyForm] = useState(toPropertyForm());
    const [propertyErrors, setPropertyErrors] = useState({});
    const [parcelCandidates, setParcelCandidates] = useState(null);
    const selectedArea = selectedId === district.id ? (_a = district.area) !== null && _a !== void 0 ? _a : undefined : undefined;
    const mapSelectedAreaId = selectedArea ? selectedArea.id : null;
    // --- Map refs ---
    const graphicsLayerRef = useRef(null);
    const pointLayerRef = useRef(null);
    const parcelSketchViewModelRef = useRef(null);
    const isActivelyEditingParcelRef = useRef(false);
    const editingPropertyRef = useRef(null);
    // --- Change detection ---
    const getChangedEdits = (edits, items, getField) => Array.from(edits.entries()).filter(([id, editedValue]) => {
        const original = items.find(item => item.id === id);
        if (!original)
            return false;
        return JSON.stringify(getField(original)) !== JSON.stringify(editedValue);
    });
    // --- Handlers ---
    const handleGeometryChange = (id, geometry) => {
        const newEdits = new Map(editedGeometries);
        newEdits.set(id, geometry);
        setEditedGeometries(newEdits);
    };
    const handleParcelPointChange = (parcelId, point) => {
        const newEdits = new Map(editedParcelPoints);
        newEdits.set(parcelId, point);
        setEditedParcelPoints(newEdits);
    };
    const onParcelPointChangeRef = useRef(handleParcelPointChange);
    useEffect(() => {
        onParcelPointChangeRef.current = handleParcelPointChange;
    }, [editedParcelPoints]);
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        const areaUpdates = getChangedEdits(editedGeometries, district.area ? [district.area] : [], a => a.geometry)
            .map(([id, geometry]) => ({ id, geometry }));
        const parcelUpdates = getChangedEdits(editedParcelPoints, parcels, p => p.point)
            .map(([id, point]) => { var _a, _b; return ({ id, point, kind: (_b = (_a = parcels.find((p) => p.id === id).kind) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '' }); });
        if (areaUpdates.length === 0 && parcelUpdates.length === 0) {
            alert('No changes to save.');
            return;
        }
        for (const update of areaUpdates) {
            yield updateAreaGeometry(update)();
        }
        for (const update of parcelUpdates) {
            yield updateParcel(update)();
        }
        setEditedGeometries(new Map());
        setEditedParcelPoints(new Map());
        alert('Changes saved successfully!');
        reloadAll();
    });
    const handleDiscard = () => {
        setEditedGeometries(new Map());
        setEditedParcelPoints(new Map());
        setSelectedId(null);
    };
    const buildManualCreateData = () => {
        if (!parcelPoint) {
            alert('Please click on the map to set a location for the parcel.');
            return null;
        }
        if (Object.values(parcelForm).some(v => !v) || !selectedParcelKind) {
            alert('Please fill in all required fields: Address, City, State, ZIP, and Property Type.');
            return null;
        }
        const address = `${parcelForm.street}, ${parcelForm.city}, ${parcelForm.state}, ${parcelForm.zip}`;
        return { point: parcelPoint, address, kind: selectedParcelKind };
    };
    const handleCreateParcel = () => __awaiter(void 0, void 0, void 0, function* () {
        const createData = buildManualCreateData();
        if (!createData)
            return;
        setIsSaving(true);
        try {
            if (parcelCandidates === null) {
                const nearby = yield findNearbyParcels(createData.point)();
                if (nearby.length > 0) {
                    setParcelCandidates(nearby);
                    return;
                }
            }
            const res = yield createParcel(createData)();
            handleParcelCreated(propertyFromCreateResult(res));
        }
        finally {
            setIsSaving(false);
        }
    });
    const handleExistingParcelSelected = (parcel) => {
        setParcelCandidates(null);
        setIsAddingParcel(false);
        setParcelForm(emptyParcelForm);
        setParcelPoint(null);
        setIsAddressModalOpen(false);
        const property = properties.find(p => p.parcel.id === parcel.id);
        if (property) {
            handleSelectPropertyOnMap(property);
            return;
        }
        const view = mapCore.mapView;
        if (view) {
            view.goTo({ center: parcel.point, zoom: Math.max(view.zoom, 17) }, { duration: 500 }).catch(() => { });
        }
        reloadAll();
    };
    const handleCancelParcel = () => {
        setIsAddingParcel(false);
        setParcelForm(emptyParcelForm);
        setSelectedParcelKind('');
        setParcelPoint(null);
        setParcelCandidates(null);
    };
    const handleStartAddParcel = () => {
        setIsAddingParcel(true);
        setParcelForm(emptyParcelForm);
        setParcelPoint(null);
    };
    const handleParcelCreated = (form) => {
        setIsAddingParcel(false);
        setParcelForm(emptyParcelForm);
        setParcelPoint(null);
        setSelectedParcel(null);
        setPropertyForm(form);
        setIsPropertyModalOpen(true);
        reloadAll();
    };
    const handlePropertySubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        setPropertyErrors({});
        if (!('id' in propertyForm))
            return;
        const [result, data] = yield updateProperty(propertyForm);
        if (result) {
            setIsPropertyModalOpen(false);
            setPropertyForm(toPropertyForm());
            setSelectedParcel(null);
            upsertProperty(data);
        }
        else {
            setPropertyErrors(expand(data)[1]);
        }
    });
    const handleEditProperty = (property) => {
        setSelectedParcel(property.parcel);
        setPropertyForm(toPropertyForm(property));
        setPropertyErrors({});
        setIsPropertyModalOpen(true);
    };
    const handlePropertyModalClose = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsPropertyModalOpen(false);
        setSelectedParcel(null);
        setPropertyForm(toPropertyForm());
    });
    const handleSelectPropertyOnMap = (property) => {
        var _a, _b;
        const view = mapCore.mapView;
        if (!view || !((_b = (_a = property.parcel) === null || _a === void 0 ? void 0 : _a.point) === null || _b === void 0 ? void 0 : _b.length))
            return;
        const [lon, lat] = property.parcel.point;
        view.goTo({ center: [lon, lat], zoom: Math.max(view.zoom, 17) }, { duration: 500 }).then(() => {
            const screenPoint = view.toScreen(geographicPoint(lon, lat));
            if (screenPoint) {
                mapLayers.setSelectedItem(Object.assign(Object.assign({}, property), { screenX: screenPoint.x + 20, screenY: screenPoint.y - 20 }));
            }
        }).catch(() => { });
    };
    // --- Display data (merge originals with edits) ---
    // Memoize to prevent unnecessary graphics layer rebuilds that would
    // destroy SketchViewModel editing targets on unrelated re-renders.
    const displayAreas = useMemo(() => (district.area ? [district.area] : []).map((a) => {
        const editedGeometry = editedGeometries.get(a.id);
        return editedGeometry ? Object.assign(Object.assign({}, a), { geometry: editedGeometry }) : a;
    }), [district.area, editedGeometries]);
    const boundaryArea = useMemo(() => editMode === 'parcels' ? district.area : null, [editMode, district]);
    // --- Map hooks ---
    const mapCore = useMapCore({
        config,
        tileLayers,
        areas: displayAreas,
        graphicsLayerRef,
        additionalLayers: [],
    });
    // --- Viewport-based property loading ---
    const { items: properties, isLoading: isLoadingProperties, upsert: upsertProperty, reload: reloadAll, } = useViewportLoad({
        'mapView': mapCore.mapView,
        'mapReady': mapCore.mapReady,
        'fetchFn': getProperties,
        'filtersDefault': filtersDefault(),
        'filtersGeo': filtersGeoNone,
    });
    const parcels = useMemo(() => properties.map(p => p.parcel), [properties]);
    const hasChanges = useMemo(() => getChangedEdits(editedGeometries, district.area ? [district.area] : [], a => a.geometry).length > 0 ||
        getChangedEdits(editedParcelPoints, parcels, p => p.point).length > 0, [editedGeometries, district, editedParcelPoints, parcels]);
    // --- Legend state ---
    const { isLegendOpen, toggleLegend, closeLegend, hiddenKinds, uniqueKinds, toggleKind } = useLegend();
    const filteredProperties = useMemo(() => {
        if (!propertySearch)
            return properties;
        const q = propertySearch.toLowerCase();
        return properties.filter(p => p.title.toLowerCase().includes(q) ||
            p.parcel.address.toLowerCase().includes(q));
    }, [properties, propertySearch]);
    const displayProperties = useMemo(() => properties.map((property) => {
        const editedPoint = editedParcelPoints.get(property.parcel.id);
        return editedPoint
            ? Object.assign(Object.assign({}, property), { parcel: Object.assign(Object.assign({}, property.parcel), { point: editedPoint }) }) : property;
    }), [properties, editedParcelPoints]);
    const mapEditing = useMapEditing({
        mapView: mapCore.mapView,
        editMode,
        selectedId: editMode === 'areas' ? mapSelectedAreaId : null,
        areas: displayAreas,
        onGeometryChange: handleGeometryChange,
        boundaryArea,
    });
    const mapLayers = useMapLayers({
        mapView: mapCore.mapView,
        mapReady: mapCore.mapReady,
        areas: displayAreas,
        items: displayProperties,
        symbols: config.symbols,
        editMode,
        selectedId: editMode === 'areas' ? mapSelectedAreaId : null,
        configZoom: config.zoom,
        hiddenKinds,
        mapItem: (x) => (Object.assign(Object.assign({}, x), { 'point': x.parcel.point, 'kind': x.parcel.kind && Object.assign(Object.assign({}, x.parcel.kind), { 'id': x.parcel.kind.name }) })),
        mapItemToMarker: (x) => x.parcel.kind && Object.assign(Object.assign({}, x.parcel.kind), { 'id': x.parcel.kind.name }),
        mapItemToAttributes: mapPropertyToMapAttributes,
    });
    usePointPlacement({
        mapView: mapCore.mapView,
        isPlacingPoint: isAddingParcel,
        onPointPlaced: (point) => setParcelPoint(point),
        placedPoint: parcelPoint,
        pointLayerRef,
    });
    // --- Parcel editing sketch view model ---
    useEffect(() => {
        const view = mapCore.mapView;
        const propertiesLayer = mapLayers.propertiesLayerRef.current;
        if (!view || !propertiesLayer)
            return;
        view.when(() => {
            const parcelSketchViewModel = new SketchViewModel({
                view,
                layer: propertiesLayer,
                updateOnGraphicClick: false,
                defaultUpdateOptions: {
                    tool: "move",
                    enableRotation: false,
                    enableScaling: false,
                    toggleToolOnClick: false
                }
            });
            parcelSketchViewModelRef.current = parcelSketchViewModel;
            parcelSketchViewModel.on("update", (event) => {
                var _a, _b;
                if (event.state === "start") {
                    isActivelyEditingParcelRef.current = true;
                }
                if (event.state === "active") {
                    isActivelyEditingParcelRef.current = true;
                    if (((_a = event.toolEventInfo) === null || _a === void 0 ? void 0 : _a.type) === "move-start") {
                        mapLayers.setSelectedItem(null);
                    }
                }
                if (event.state === "complete" || (event.state === "active" && ((_b = event.toolEventInfo) === null || _b === void 0 ? void 0 : _b.type) === "move-stop")) {
                    if (event.graphics.length > 0) {
                        const graphic = event.graphics[0];
                        const parcelId = (graphic === null || graphic === void 0 ? void 0 : graphic.getAttribute) ? graphic.getAttribute('parcelId') : null;
                        const geometry = graphic.geometry;
                        if (parcelId && isPoint(geometry)) {
                            const point = assertGeographic(geometry);
                            if (onParcelPointChangeRef.current) {
                                onParcelPointChangeRef.current(parcelId, [point.longitude, point.latitude]);
                            }
                            if (event.state === "complete" && editingPropertyRef.current) {
                                const updatedProperty = Object.assign(Object.assign({}, editingPropertyRef.current), { parcel: Object.assign(Object.assign({}, editingPropertyRef.current.parcel), { point: [point.longitude, point.latitude] }) });
                                const screenPoint = view.toScreen(point);
                                if (screenPoint) {
                                    mapLayers.setSelectedItem(Object.assign(Object.assign({}, updatedProperty), { screenX: screenPoint.x + 20, screenY: screenPoint.y - 20 }));
                                }
                            }
                        }
                    }
                    if (event.state === "complete") {
                        isActivelyEditingParcelRef.current = false;
                    }
                }
            });
        });
        return () => {
            if (parcelSketchViewModelRef.current) {
                parcelSketchViewModelRef.current.destroy();
            }
        };
    }, [mapCore.mapView, mapLayers.propertiesLayerRef]);
    // --- Enhanced click handler for parcel editing mode ---
    useEffect(() => {
        const view = mapCore.mapView;
        if (!view || editMode !== 'parcels')
            return;
        const clickHandler = view.on('click', (event) => __awaiter(void 0, void 0, void 0, function* () {
            const response = yield view.hitTest(event);
            const propertyHit = response.results.find((result) => { var _a; return result.type === 'graphic' && ((_a = result.graphic.attributes) === null || _a === void 0 ? void 0 : _a.isProperty); });
            if (!propertyHit || propertyHit.type !== 'graphic') {
                mapLayers.setSelectedItem(null);
                return;
            }
            const attrs = propertyHit.graphic.attributes;
            const property = displayProperties.find(p => p.id === attrs.propertyId);
            if (!property) {
                mapLayers.setSelectedItem(null);
                return;
            }
            editingPropertyRef.current = property;
            mapLayers.setSelectedItem(Object.assign(Object.assign({}, property), { screenX: event.x + 20, screenY: event.y - 20 }));
            const parcelSketchViewModel = parcelSketchViewModelRef.current;
            if (parcelSketchViewModel) {
                try {
                    parcelSketchViewModel.update([propertyHit.graphic], { tool: "move" });
                }
                catch (_a) {
                    // Ignore
                }
            }
        }));
        return () => {
            clickHandler.remove();
        };
    }, [mapCore.mapView, editMode, displayProperties]);
    // --- Click handler for area selection in area mode ---
    useEffect(() => {
        const view = mapCore.mapView;
        if (!view || editMode !== 'areas')
            return;
        const clickHandler = view.on('click', (event) => __awaiter(void 0, void 0, void 0, function* () {
            const response = yield view.hitTest(event);
            const areaHit = response.results.find((result) => { var _a; return result.type === 'graphic' && ((_a = result.graphic.attributes) === null || _a === void 0 ? void 0 : _a.isArea); });
            if (areaHit && areaHit.type === 'graphic') {
                const areaId = areaHit.graphic.getAttribute('areaId');
                if (areaId) {
                    const ownerDistrict = [district].find(d => { var _a; return ((_a = d.area) === null || _a === void 0 ? void 0 : _a.id) === areaId; });
                    if (ownerDistrict)
                        setSelectedId(ownerDistrict.id);
                }
            }
        }));
        return () => {
            clickHandler.remove();
        };
    }, [mapCore.mapView, editMode, district]);
    return (_jsxs("div", { className: className || '', style: { padding: '0 1rem' }, children: [_jsx("div", { className: "position-relative", style: { height: 'calc(100vh - 56px)' }, children: _jsxs("div", { style: {
                        position: 'relative',
                        height: '100%',
                        width: '100%',
                    }, children: [_jsx(MapToolbar, { toolbar: useMapToolbar({
                                basemap: mapCore.basemap,
                                setBasemap: mapCore.setBasemap,
                                tileLayerVisibility: mapCore.tileLayerVisibility,
                                setTileLayerVisibility: mapCore.setTileLayerVisibility,
                                handleZoomIn: mapCore.handleZoomIn,
                                handleZoomOut: mapCore.handleZoomOut,
                                handleRecenter: mapCore.handleRecenter,
                                tileLayers,
                                showBasemapSelector: config.showBasemapSelector,
                            }), additionalTools: mapSelectedAreaId && editMode === 'areas' ? (_jsx(EditingToolbar, { onSubdivide: mapEditing.handleSubdivide, onSimplify: mapEditing.handleUnsubdivide, onRotateLeft: () => mapEditing.handleRotate(-10), onRotateRight: () => mapEditing.handleRotate(10), onAddRectangle: mapEditing.handleAddRectangle, onDelete: mapEditing.handleDelete })) : null, secondaryTools: _jsxs(_Fragment, { children: [_jsxs("div", { className: "btn-group btn-group-sm shadow-sm", children: [_jsxs("button", { className: `btn ${editMode === 'areas' ? 'btn-primary' : 'btn-light'}`, onClick: () => {
                                                    setEditMode('areas');
                                                    setIsAddingParcel(false);
                                                    setParcelPoint(null);
                                                }, title: "Area mode", children: [_jsx("i", { className: "bi bi-bounding-box me-1" }), "Edit Area"] }), _jsxs("button", { className: `btn ${editMode === 'parcels' ? 'btn-primary' : 'btn-light'}`, onClick: () => setEditMode('parcels'), title: "Parcel mode", children: [_jsx("i", { className: "bi bi-geo-alt me-1" }), "Edit Parcel"] })] }), _jsxs("button", { className: `btn btn-sm shadow-sm ${isPropertiesPanelOpen ? 'btn-primary' : 'btn-light'}`, onClick: () => setIsPropertiesPanelOpen(prev => !prev), title: "Properties", children: [_jsx("i", { className: "bi bi-buildings me-1" }), "Properties List"] }), uniqueKinds.length > 0 && (_jsxs("button", { className: `btn btn-sm shadow-sm ${isLegendOpen ? 'btn-primary' : 'btn-light'}`, onClick: toggleLegend, title: "Toggle Legend", children: [_jsx("i", { className: "bi bi-map me-1" }), "Legend"] }))] }) }), isLegendOpen && (_jsx(LegendPanel, { kinds: uniqueKinds, hiddenKinds: hiddenKinds, onToggleKind: toggleKind, onClose: closeLegend, style: isPropertiesPanelOpen ? { left: 398 } : undefined })), isPropertiesPanelOpen && (_jsx("div", { className: "card shadow", style: {
                                position: 'absolute',
                                top: '90px',
                                left: '50px',
                                zIndex: 2,
                                width: '340px',
                                maxHeight: 'calc(100% - 110px)',
                                overflowY: 'auto',
                            }, children: _jsxs("div", { className: "card-body p-3", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsx("h6", { className: "fw-semibold mb-0", children: "Properties" }), _jsx("button", { className: "btn btn-sm btn-close", onClick: () => setIsPropertiesPanelOpen(false) })] }), _jsx("button", { onClick: () => { setIsAddressModalOpen(true); setIsPropertiesPanelOpen(false); }, className: "btn btn-primary btn-sm w-100 mb-2", children: "Add Parcel By Lookup" }), _jsx("button", { onClick: () => { handleStartAddParcel(); setIsPropertiesPanelOpen(false); }, className: "btn btn-secondary btn-sm w-100 mb-3", children: "Add Parcel Manually" }), _jsx(Input, { kind: "text", label: "Search properties...", size: "sm", className: "mb-3", value: propertySearch, onChange: setPropertySearch }), isLoadingProperties && (_jsx("h6", { className: "fw-semibold mb-2 small", children: "Loading..." })), filteredProperties.length === 0 && !isLoadingProperties && (_jsx("p", { className: "small text-muted", children: "No properties found" })), filteredProperties.map((prop) => (_jsx(Card, { className: "mb-2 rounded p-2", style: { cursor: 'pointer' }, onClick: () => handleSelectPropertyOnMap(prop), children: _jsxs("div", { className: "d-flex justify-content-between align-items-start", children: [_jsxs("div", { children: [_jsx("p", { className: "small mb-1 fw-semibold", children: prop.title }), prop.parcel.address && (_jsx("p", { className: "small text-muted mb-0", children: prop.parcel.address }))] }), _jsx("span", { className: "badge bg-secondary mx-2", children: prop.status.display }), _jsx("button", { className: "btn btn-sm btn-outline-primary", onClick: (e) => { e.stopPropagation(); handleEditProperty(prop); setIsPropertiesPanelOpen(false); }, children: "Edit" })] }) }, prop.id)))] }) })), hasChanges && (_jsx(SaveToolbar, { onSave: handleSave, onDiscard: handleDiscard })), isAddingParcel && (_jsx(PlacementBanner, { message: "Click on the map to place the parcel location" })), isAddingParcel && (_jsx("div", { className: "card shadow", style: {
                                position: 'absolute',
                                top: '90px',
                                left: '50px',
                                zIndex: 2,
                                width: '300px',
                                maxHeight: 'calc(100% - 110px)',
                                overflowY: 'auto',
                            }, children: _jsxs("div", { className: "card-body p-3", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsx("h6", { className: "fw-semibold mb-0", children: parcelCandidates ? 'Possible Existing Parcels' : 'New Parcel' }), _jsx("button", { className: "btn btn-sm btn-close", onClick: handleCancelParcel })] }), parcelCandidates ? (_jsxs(_Fragment, { children: [_jsxs("p", { className: "small mb-3", children: ["We found ", parcelCandidates.length === 1 ? 'an existing parcel' : `${parcelCandidates.length} existing parcels`, " near this location. Did you mean ", parcelCandidates.length === 1 ? 'it' : 'one of these', "?"] }), parcelCandidates.map(parcel => (_jsxs("div", { className: "border rounded p-2 mb-2", children: [_jsx("div", { className: "fw-semibold small", children: parcel.address }), parcel.kind && (_jsx("div", { className: "small text-muted mb-2", children: parcel.kind.displayName })), _jsx("button", { className: "btn btn-sm btn-outline-primary w-100", onClick: () => handleExistingParcelSelected(parcel), disabled: isSaving, children: "Edit this parcel" })] }, parcel.id))), _jsxs("div", { className: "d-flex gap-2 mt-3", children: [_jsx("button", { onClick: () => setParcelCandidates(null), disabled: isSaving, className: "btn btn-outline-secondary btn-sm", children: "Back" }), _jsx("button", { onClick: handleCreateParcel, disabled: isSaving, className: "btn btn-primary btn-sm flex-grow-1", children: isSaving ? 'Adding...' : 'Add anyway' })] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "mb-2", children: _jsx(Input, { kind: "text", label: "Address", value: parcelForm.street, onChange: (v) => setParcelForm(prev => (Object.assign(Object.assign({}, prev), { street: v }))) }) }), _jsx("div", { className: "mb-2", children: _jsx(Input, { kind: "text", label: "City", value: parcelForm.city, onChange: (v) => setParcelForm(prev => (Object.assign(Object.assign({}, prev), { city: v }))) }) }), _jsx("div", { className: "mb-2", children: _jsx(Dropdown, { label: "State", options: Promise.resolve(usStates), optionKey: (state) => state.name, optionLabel: (state) => state.abbreviation, value: parcelForm.state, onChange: (v) => setParcelForm(prev => (Object.assign(Object.assign({}, prev), { state: v || '' }))) }) }), _jsx("div", { className: "mb-2", children: _jsx(Input, { kind: "text", label: "Zip", value: parcelForm.zip, onChange: (v) => setParcelForm(prev => (Object.assign(Object.assign({}, prev), { zip: v.replace(/\D/g, '') }))) }) }), _jsx("div", { className: "mb-2", children: _jsx(Dropdown, { label: "Property Type", options: parcelKindsPromise, optionKey: (pk) => pk.name, optionLabel: (pk) => pk.displayName, value: selectedParcelKind, onChange: (name) => setSelectedParcelKind(name || ''), required: true }) }), _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label small", children: "Location" }), parcelPoint ? (_jsxs("p", { className: "small text-success mb-0", children: ["Point set: [", parcelPoint[0].toFixed(6), ", ", parcelPoint[1].toFixed(6), "]"] })) : (_jsx("p", { className: "small text-muted mb-0", children: "Click on the map to set the parcel location" }))] }), _jsxs("div", { className: "d-flex gap-2", children: [_jsx("button", { onClick: handleCreateParcel, disabled: isSaving || !parcelPoint || !selectedParcelKind, className: "btn btn-primary btn-sm flex-grow-1", children: isSaving ? 'Saving...' : 'Save Parcel' }), _jsx("button", { onClick: handleCancelParcel, disabled: isSaving, className: "btn btn-secondary btn-sm", children: "Cancel" })] })] }))] }) })), _jsx("div", { ref: mapCore.mapDiv, className: 'w-100 h-100', style: {
                                cursor: isAddingParcel ? 'crosshair' : 'default'
                            } }), _jsx(MapAttribution, { mapView: mapCore.mapView }), mapLayers.selectedItem && !(editMode === 'areas' && mapSelectedAreaId) && (_jsx(PropertyPopup, { position: mapLayers.selectedItem, property: konstPromise(mapLayers.selectedItem), onClose: () => mapLayers.setSelectedItem(null), containerWidth: (_b = mapCore.mapDiv.current) === null || _b === void 0 ? void 0 : _b.clientWidth, showSave: false, children: _jsxs("div", { className: "card-body py-2 px-3 border-top", children: [_jsx("span", { className: `badge mb-2 ${mapLayers.selectedItem.status.name === 'active' ? 'bg-success' : 'bg-secondary'}`, children: mapLayers.selectedItem.status.display }), _jsx("div", { className: "mb-2", children: _jsx(Dropdown, { label: "Type", options: parcelKindsPromise, optionKey: (pk) => pk.name, optionLabel: (pk) => pk.displayName, value: (_d = (_c = mapLayers.selectedItem.parcel.kind) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : '', onChange: (val) => __awaiter(void 0, void 0, void 0, function* () {
                                                const sel = mapLayers.selectedItem;
                                                yield updateParcel({ id: sel.parcel.id, point: sel.parcel.point, kind: val || '' })();
                                                const kinds = yield parcelKindsPromise;
                                                const pk = kinds.find(k => k.name === val);
                                                const updatedParcel = Object.assign(Object.assign({}, sel.parcel), { kind: pk !== null && pk !== void 0 ? pk : null });
                                                const { screenX, screenY } = sel, base = __rest(sel, ["screenX", "screenY"]);
                                                const clean = Object.assign(Object.assign({}, base), { parcel: updatedParcel });
                                                mapLayers.setSelectedItem(Object.assign(Object.assign({}, clean), { screenX, screenY }));
                                                upsertProperty(clean);
                                            }) }) }), _jsx("div", { className: "mb-2", children: _jsx(MultiSelect, { label: "Tags", options: propertyTagsPromise, optionKey: (t) => t.id, optionLabel: (t) => t.display, value: mapLayers.selectedItem.tags.map((t) => t.id), onChange: (val) => __awaiter(void 0, void 0, void 0, function* () {
                                                const sel = mapLayers.selectedItem;
                                                const allTags = yield propertyTagsPromise;
                                                const newTags = allTags.filter(t => val.includes(t.id));
                                                yield setPropertyTags({ propertyId: sel.id, tagIds: val })();
                                                const { screenX, screenY } = sel, base = __rest(sel, ["screenX", "screenY"]);
                                                const clean = Object.assign(Object.assign({}, base), { tags: newTags });
                                                mapLayers.setSelectedItem(Object.assign(Object.assign({}, clean), { screenX, screenY }));
                                                upsertProperty(clean);
                                            }) }) }), _jsx("button", { className: "btn btn-outline-primary btn-sm w-100", onClick: () => handleEditProperty(mapLayers.selectedItem), children: "Edit Property" })] }) }))] }) }), _jsx(AddAddressModal, { isOpen: isAddressModalOpen, onClose: () => setIsAddressModalOpen(false), onParcelCreated: handleParcelCreated, onExistingSelected: handleExistingParcelSelected }), _jsxs(Modal, { isOpen: isPropertyModalOpen, title: `Edit ${propertyForm.title} Details`, onClose: handlePropertyModalClose, children: [(selectedParcel === null || selectedParcel === void 0 ? void 0 : selectedParcel.address) && (_jsxs("p", { className: "small text-muted mb-3", children: [_jsx("strong", { children: "Parcel Address:" }), " ", selectedParcel.address] })), _jsx("div", { className: "mb-3 d-flex align-items-center justify-content-between", children: _jsxs("p", { className: "small mb-0", children: [_jsx("strong", { children: "Status:" }), " ", propertyForm.status.display] }) }), _jsx(PropertyAddEdit, { errors: propertyErrors, value: propertyForm, onChange: setPropertyForm, onSubmit: handlePropertySubmit, onCancel: handlePropertyModalClose })] })] }));
};
